import React, { useState, useMemo } from 'react'
import Select from 'react-select'
import { Tr, Td } from '../table'
import { Spinner } from '../../base-components'
import style from '../fast-configuration-modal/fastConfigurationModal.css'
import useTranslation from '../../hooks/useTranslation'
import { EquipmentPackageContent } from '../../entities/Equipment'

interface EquipmentOption {
    value: string
    label: string
}

interface FastConfigurationEquipmentAutocompleteProps {
    equipmentGroups: any[]
    equipments: EquipmentOption[]
    selectedModel: any
    setEquipments: (equipments: EquipmentOption[]) => void
    isLoading: boolean
}

const FastConfigurationEquipmentAutocomplete: React.FC<FastConfigurationEquipmentAutocompleteProps> = ({
    equipmentGroups,
    equipments,
    selectedModel,
    setEquipments,
    isLoading,
}) => {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState('')
    const [error, setError] = useState('')

    const availableEquipments = useMemo(() => {
        if (!equipmentGroups?.length) {
            return []
        }

        return equipmentGroups
            .flatMap(group => group.equipmentFamilies || [])
            .flatMap(family => family.equipments || [])
            .filter(equipment => {
                if (equipment.id.startsWith('F14') ||
                    equipment.id.startsWith('F56') ||
                    equipment.id.startsWith('K-') ||
                    equipment.id.startsWith('Z')) {
                    return false
                }
                return !(equipment.equipmentType === 'INTERIOR_COLOR' ||
                    equipment.equipmentType === 'EXTERIOR_COLOR' ||
                    equipment.equipmentType?.includes('COLOR'))
            })
    }, [equipmentGroups])

    const equipmentOptions = useMemo(() => {
        const optionsMap = new Map<string, EquipmentOption>()

        availableEquipments.forEach(equipment => {
            optionsMap.set(equipment.id, {
                value: equipment.id,
                label: equipment.id,
            })

            if (equipment.isPackage && equipment.content) {
                equipment.content.forEach((content: EquipmentPackageContent) => {
                    const packageLabel = `${content.id} (${equipment.id})`
                    optionsMap.set(packageLabel, {
                        value: equipment.id,
                        label: packageLabel,
                    })
                })
            }
        })

        return Array.from(optionsMap.values()).sort((a, b) => a.label.localeCompare(b.label))
    }, [availableEquipments])

    const filteredOptions = useMemo(() => {
        if (inputValue.length < 3) {
            return []
        }

        const inputLower = inputValue.toLowerCase()

        return equipmentOptions.filter(option => {
            if (option.label.toLowerCase().endsWith(inputLower)) {
                return true
            }

            if (option.label.toLowerCase() === inputLower) {
                return true
            }

            if (option.label.toLowerCase().startsWith(inputLower)) {
                return true
            }

            return option.label.toLowerCase().includes(inputLower)

        }).sort((a, b) => {
            const aLower = a.label.toLowerCase()
            const bLower = b.label.toLowerCase()

            const aEndsWith = aLower.endsWith(inputLower)
            const bEndsWith = bLower.endsWith(inputLower)
            if (aEndsWith && !bEndsWith) {
                return -1
            }
            if (!aEndsWith && bEndsWith) {
                return 1
            }

            const aExact = aLower === inputLower
            const bExact = bLower === inputLower
            if (aExact && !bExact) {
                return -1
            }
            if (!aExact && bExact) {
                return 1
            }

            if (a.label.length !== b.label.length) {
                return a.label.length - b.label.length
            }

            return a.label.localeCompare(b.label)
        })
    }, [equipmentOptions, inputValue])

    const matchEquipmentFromText = (text: string): string[] => {
        if (!text.trim()) {
            return []
        }

        const codes = text.split(/[\s,\n\r\t|\u00A0]+/)
            .map(code => code.trim())
            .filter(code => code.length >= 3)

        const matchedIds = new Set<string>()

        codes.forEach(code => {
            const codeLower = code.toLowerCase()

            const matches = availableEquipments.filter(eq =>
                eq.id.toLowerCase().endsWith(codeLower),
            )

            if (matches.length === 1) {
                matchedIds.add(matches[0].id)
            } else if (matches.length > 1) {
                const bestMatch = matches.reduce((best, current) => {
                    if (current.id.toLowerCase() === codeLower) {
                        return current
                    }
                    if (best.id.toLowerCase() === codeLower) {
                        return best
                    }
                    return current.id.length < best.id.length ? current : best
                })
                matchedIds.add(bestMatch.id)
            }
        })

        return Array.from(matchedIds)
    }

    const handlePaste = (event: React.ClipboardEvent) => {
        event.preventDefault()
        const pastedData = event.clipboardData.getData('Text')

        const matchedIds = matchEquipmentFromText(pastedData)
        const newOptions = matchedIds.map(id => ({ value: id, label: id }))

        const inputCodes = pastedData.split(/[\s,\n\r\t|\u00A0]+/)
            .map(code => code.trim())
            .filter(code => code.length >= 3)

        const matchedLower = matchedIds.map(id => id.toLowerCase())
        const notFoundCodes = inputCodes.filter(code =>
            !matchedLower.some(id => id === code.toLowerCase() || id.endsWith(code.toLowerCase())),
        )

        if (notFoundCodes.length > 0) {
            setError(`Equipment not found: ${notFoundCodes.join(', ')}`)
        } else {
            setError('')
        }

        setEquipments(newOptions)
        setInputValue('')
    }

    const handleSelectChange = (selected: EquipmentOption[] | null) => {
        setEquipments(selected || [])
    }

    const handleInputChange = (value: string) => {
        setInputValue(value)

        if (value.length >= 3) {
            const inputLower = value.toLowerCase()
            const isValidEquipment = availableEquipments.some(eq =>
                eq.id.toLowerCase().endsWith(inputLower) ||
                eq.id.toLowerCase() === inputLower ||
                eq.id.toLowerCase().startsWith(inputLower) ||
                eq.id.toLowerCase().includes(inputLower),
            )

            if (!isValidEquipment) {
                setError(`Equipment not found: ${value}`)
            } else {
                setError('')
            }
        } else {
            setError('')
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && inputValue.length >= 3) {
            const inputLower = inputValue.toLowerCase()
            const isValidEquipment = availableEquipments.some(eq =>
                eq.id.toLowerCase().endsWith(inputLower) ||
                eq.id.toLowerCase() === inputLower ||
                eq.id.toLowerCase().startsWith(inputLower) ||
                eq.id.toLowerCase().includes(inputLower),
            )

            if (!isValidEquipment) {
                setError(`Equipment not found: ${inputValue}`)
                event.preventDefault()
            }
        }
    }

    return (
        <Tr>
            <Td className={style.noBorder}>
                <div className={style.explanationContainer}>
                    <h4 className={style.explanationHeading}>
                        {t('fastConfigurationModal.equipmentsTitle')}
                    </h4>
                    <span className={style.explanation}>
                        {t('fastConfigurationModal.equipmentsExplanation')}
                    </span>
                </div>
            </Td>
            <Td className={style.noBorder}>
                {isLoading ? (
                    <div className={style.spinnerContainer}>
                        <Spinner />
                    </div>
                ) : (
                    <div onPaste={handlePaste}>
                        <Select<EquipmentOption, true>
                            options={filteredOptions}
                            value={equipments}
                            onChange={handleSelectChange}
                            onInputChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            isMulti
                            isDisabled={selectedModel == null}
                            isSearchable
                            isClearable
                            placeholder=""
                            className={style.input}
                            classNamePrefix="fastConfig"
                            components={{
                                DropdownIndicator: () => null,
                                IndicatorSeparator: () => null,
                            }}
                            noOptionsMessage={() =>
                                inputValue.length < 3 && equipments.length === 0
                                    ? t('fastConfigurationModal.atLeastThreeCharacters')
                                    : equipments.length === 0
                                    ? t('fastConfigurationModal.noEquipmentsFound')
                                    : null
                            }
                        />
                        {error && <div className={style.error}>{error}</div>}
                    </div>
                )}
            </Td>
        </Tr>
    )
}

export default FastConfigurationEquipmentAutocomplete
